import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { IconButton, Popover, Tooltip } from "@mui/material";
import EmojiPicker, { type EmojiClickData, Theme } from "emoji-picker-react";
import { useRef, useState } from "react";

interface EmojiPickerButtonProps {
	onEmojiSelect: (emoji: string) => void;
	disabled?: boolean;
	size?: "small" | "medium";
}

/**
 * Reusable emoji picker button component.
 * Opens a popover with searchable emoji grid when clicked.
 */
export const EmojiPickerButton = ({
	onEmojiSelect,
	disabled = false,
	size = "small",
}: EmojiPickerButtonProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleEmojiClick = (emojiData: EmojiClickData) => {
		onEmojiSelect(emojiData.emoji);
		handleClose();
	};

	const open = Boolean(anchorEl);

	return (
		<>
			<Tooltip title="Insert emoji">
				<IconButton
					ref={buttonRef}
					onClick={handleClick}
					disabled={disabled}
					size={size}
					data-component="emoji-picker-button"
				>
					<EmojiEmotionsIcon fontSize={size} />
				</IconButton>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				data-component="emoji-picker-popover"
			>
				<EmojiPicker
					onEmojiClick={handleEmojiClick}
					theme={Theme.AUTO}
					searchPlaceholder="Search emoji..."
					width={350}
					height={400}
				/>
			</Popover>
		</>
	);
};
